import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

const KINDS = [
  { id: "workbook", label: "Guide + AI workbook", sub: "AI writes a matching workbook from a guide, then bundles the two" },
  { id: "pair", label: "Pick titles", sub: "Choose any titles to sell together" },
  { id: "series", label: "Complete series", sub: "Every title in a category, kept up to date" },
];

export const NewBundleDialog = ({ open, onOpenChange }) => {
  const nav = useNavigate();
  const [kind, setKind] = useState("workbook");
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [guide, setGuide] = useState("");
  const [picked, setPicked] = useState([]);
  const [cat, setCat] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/categories").then((r) => setCats(r.data));
  }, [open]);

  const hasWorkbook = new Set(products.map((p) => p.workbook_of).filter(Boolean));
  const guides = products.filter((p) => p.product_type !== "workbook" && !hasWorkbook.has(p.id) && p.chapter_total);
  const toggle = (id) => setPicked((x) => (x.includes(id) ? x.filter((y) => y !== id) : [...x, id]));

  const create = async () => {
    setBusy(true);
    try {
      let id;
      if (kind === "workbook") {
        if (!guide) throw new Error("Choose the guide");
        const r = await api.post("/bundles/workbook", { guide_id: guide, bundle: true });
        toast.success(`Writing the workbook — ${r.data.chapters} chapters`);
        id = r.data.bundle_id;
      } else {
        const body = kind === "series" ? { kind, category_id: cat } : { kind, product_ids: picked };
        id = (await api.post("/bundles", body)).data.id;
      }
      onOpenChange(false);
      nav(`/bundles/${id}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl rounded-none" data-testid="new-bundle-dialog">
        <DialogHeader><DialogTitle className="font-display text-3xl font-normal">New bundle</DialogTitle></DialogHeader>
        <div className="grid gap-2">
          {KINDS.map((k) => (
            <button key={k.id} onClick={() => setKind(k.id)} data-testid={`bundle-kind-${k.id}`}
              className={`text-left border px-4 py-3 transition-colors ${kind === k.id ? "border-[#5E2B38] bg-[#F5EFE6]" : "border-[#2A2325]/15 hover:bg-[#FBF8F3]"}`}>
              <div className="font-medium">{k.label}</div>
              <div className="text-xs text-[#5C5255]">{k.sub}</div>
            </button>
          ))}
        </div>
        <div className="mt-2">
          {kind === "workbook" && (
            <select value={guide} onChange={(e) => setGuide(e.target.value)} className="w-full h-10 border border-[#2A2325]/20 bg-white px-2 text-sm" data-testid="bundle-guide-select">
              <option value="">Choose the guide…</option>
              {guides.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
            </select>
          )}
          {kind === "pair" && (
            <div className="max-h-64 overflow-y-auto border border-[#2A2325]/15 divide-y divide-[#2A2325]/10" data-testid="bundle-pick-list">
              {products.map((p) => (
                <label key={p.id} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-[#FBF8F3]">
                  <input type="checkbox" checked={picked.includes(p.id)} onChange={() => toggle(p.id)} data-testid={`bundle-pick-${p.id}`} />
                  <span className="flex-1">{p.title}</span>
                  {!p.files?.pdf_path && <span className="text-[10px] tracking-widest text-[#9A6B2F]">NOT BUILT</span>}
                </label>
              ))}
            </div>
          )}
          {kind === "series" && (
            <select value={cat} onChange={(e) => setCat(e.target.value)} className="w-full h-10 border border-[#2A2325]/20 bg-white px-2 text-sm" data-testid="bundle-category-select">
              <option value="">Choose the category…</option>
              {cats.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.product_count})</option>)}
            </select>
          )}
          {kind === "workbook" && <p className="text-xs text-[#5C5255] mt-2">Uses your OpenRouter credit: one AI call per chapter. The workbook is saved as its own title in the same category.</p>}
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" className="rounded-none" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="rounded-none bg-[#5E2B38] hover:bg-[#4A2029]" disabled={busy || (kind === "workbook" ? !guide : kind === "pair" ? picked.length < 2 : !cat)} onClick={create} data-testid="bundle-create-btn">
            {busy ? "Creating…" : kind === "workbook" ? "Write workbook & create bundle" : "Create bundle"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
